// src/pages/HomePage.jsx
//
// 대시보드 홈 — DashboardLayout 안에서 렌더되므로 자체 상단바 없이
// 콘텐츠(인사 행 + 업무 카드 그리드)만 담당한다.

import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import FactoryIcon from '@mui/icons-material/Factory';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import InventoryIcon from '@mui/icons-material/Inventory';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { designTokens } from '../theme.ts';

const C = {
  canvas: '#F6F7F8',
  surface: '#FFFFFF',
  border: '#E4E7EA',
  ink: '#171A1E',
  sub: '#6B7178',
  accent: '#E2451C',
  accentHover: '#C93A14',
  chipBg: '#F2F4F5',
  accentTint: '#FDEDE8',
};

const SANS = designTokens.fonts.sans;

const ICON_SX = { fontSize: 22 };

const sections = [
  {
    title: '견적서 작성',
    desc: '진영/문/비교 견적서를 작성하고 Excel 또는 PDF로 내보냅니다.',
    icon: <DescriptionIcon sx={ICON_SX} />,
    category: '견적',
    path: '/dashboard/estimate/create',
  },
  {
    title: '견적서 조회/수정',
    desc: '작성된 견적서를 조회하고 수정합니다.',
    icon: <ListAltIcon sx={ICON_SX} />,
    category: '견적',
    path: '/dashboard/estimate/read',
  },
  {
    title: '생산요청서 (렌트)',
    desc: '렌트 제품의 생산요청서를 조회합니다.',
    icon: <FactoryIcon sx={ICON_SX} />,
    category: '생산',
    path: '/dashboard/product-req-rents',
  },
  {
    title: '제품 모니터',
    desc: '제품 재고 현황을 실시간으로 모니터링합니다.',
    icon: <InventoryIcon sx={ICON_SX} />,
    category: '생산',
    path: '/dashboard/product/monitor',
  },
  {
    title: '반품 요청서',
    desc: '반품 요청을 등록하고 처리 현황을 관리합니다.',
    icon: <AssignmentReturnIcon sx={ICON_SX} />,
    category: '반품',
    path: '/dashboard/return-request/form',
  },
  {
    title: '상담 기록',
    desc: '고객 상담 내용을 기록하고 이력을 관리합니다.',
    icon: <SupportAgentIcon sx={ICON_SX} />,
    category: '상담',
    path: '/dashboard/customerservice/calllog',
  },
  {
    title: '세금계산서 발행 목록',
    desc: '등록된 세금계산서 발행 건과 회차별 일정을 관리합니다.',
    icon: <LocalShippingIcon sx={ICON_SX} />,
    category: '세금계산서',
    path: '/dashboard/container-rental',
  },
  {
    title: '세금계산서 발행 등록',
    desc: '견적서를 불러와 등록하면 회차별 세금계산서 발행 일정이 자동으로 생성됩니다.',
    icon: <AddBusinessIcon sx={ICON_SX} />,
    category: '세금계산서',
    path: '/dashboard/container-rental/create',
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth0();

  const today = new Date();
  const stamp = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
  const weekday = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][today.getDay()];

  const displayName = isAuthenticated && user ? user.name || user.nickname : null;

  return (
    // 배경(#F6F7F8)은 DashboardLayout 이 담당하므로 여기서는 콘텐츠만 렌더한다.
    <Box
      sx={{
        fontFamily: SANS,
        maxWidth: 1120,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {/* 인사 행 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: SANS,
              fontWeight: 700,
              fontSize: 26,
              lineHeight: 1.3,
              letterSpacing: '-0.01em',
              color: C.ink,
            }}
          >
            {displayName ? `안녕하세요, ${displayName}님` : '안녕하세요'}
          </Typography>
          <Typography sx={{ mt: 0.75, fontFamily: SANS, fontSize: 14, color: C.sub }}>
            오늘 처리할 업무를 선택하세요.
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: 13,
            color: C.sub,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {stamp} · {weekday}
        </Typography>
      </Box>

      {/* 업무 카드 그리드 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: '20px',
        }}
      >
        {sections.map((s) => (
          <TaskCard key={s.path} section={s} onClick={() => navigate(s.path)} />
        ))}
      </Box>
    </Box>
  );
}

function TaskCard({ section, onClick }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: 'pointer',
        outline: 'none',
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: '12px',
        p: 3,
        minHeight: 180,
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        transition:
          'border-color 180ms ease, box-shadow 180ms ease, transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        '&:hover, &:focus-visible': {
          borderColor: C.accent,
          boxShadow: '0 8px 20px rgba(226,69,28,0.10)',
          '& .task-card-icon': {
            bgcolor: C.accentTint,
            color: C.accent,
          },
          '& .task-card-chip': {
            bgcolor: C.accentTint,
            color: C.accentHover,
          },
        },
      }}
    >
      <Box
        className="task-card-icon"
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: '10px',
          bgcolor: C.chipBg,
          color: C.ink,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 180ms ease, color 180ms ease',
        }}
      >
        {section.icon}
      </Box>

      <Typography
        sx={{
          fontFamily: SANS,
          fontWeight: 700,
          fontSize: 16,
          lineHeight: 1.35,
          color: C.ink,
        }}
      >
        {section.title}
      </Typography>

      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: 13,
          lineHeight: 1.55,
          color: C.sub,
        }}
      >
        {section.desc}
      </Typography>

      <Box sx={{ mt: 'auto', pt: 0.5 }}>
        <Box
          component="span"
          className="task-card-chip"
          sx={{
            display: 'inline-block',
            fontFamily: SANS,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            color: C.sub,
            bgcolor: C.chipBg,
            borderRadius: '6px',
            padding: '3px 9px',
            transition: 'background-color 180ms ease, color 180ms ease',
          }}
        >
          {section.category}
        </Box>
      </Box>
    </Box>
  );
}
